import { CateCity } from '@/context/Cities'
import { CanonicalFilter } from '@/types/filter'
import { StateOfMaintenanceFilter } from '@/types/graphql-generated-types'
import citiesMapper, { findCity } from '@/utils/cities-mapper'
import listingFacilities from '@/utils/listing-facilities'

import { isLocaleEnabledFor } from './locale-variables'

export type PageType =
  | 'not-found'
  | 'detail'
  | 'zone'
  | 'canonical-filter'
  | 'city'
  | 'favorites'
  | 'buyer-interest'
  | 'suggested'

// TODO: Avoid having to specify slug for locales that do not support the canonical filter
export const canonicalFilters: CanonicalFilter[] = [
  { filter: { onlyWithTerrace: true }, id: 'terrace', slug: { en: 'terrace', fr: '', it: 'terrazzo' } },
  { filter: { onlyWithBalcony: true }, id: 'balcony', slug: { en: 'balcony', fr: '', it: 'balcone' } },
  {
    filter: { byRooms: { from: 1, to: 1 } },
    id: 'studios',
    slug: { en: 'studios', fr: '', it: 'monolocali' },
  },
  {
    filter: { byRooms: { from: 2, to: 2 } },
    id: 'one-bedroom-apartments',
    slug: { en: 'one-bedroom-apartments', fr: '', it: 'bilocali' },
  },
  {
    filter: { byRooms: { from: 3, to: 3 } },
    id: 'two-bedroom-apartments',
    slug: { en: 'two-bedroom-apartments', fr: '', it: 'trilocali' },
  },
  {
    filter: { byStateOfMaintenance: [StateOfMaintenanceFilter.New] },
    id: 'new-constructions',
    slug: { en: 'new-constructions', fr: '', it: 'nuove-costruzioni' },
  },
]

const isFilteredPage = (locale: Locale, queryParams) =>
  isLocaleEnabledFor('filteredPages', locale) &&
  queryParams.length === 1 &&
  canonicalFilters.some((cF) => queryParams[0] === cF.slug[locale])

const isZonePage = (city, queryParams, cities: CateCity[]) => {
  const currentCity = findCity(city, cities)
  const currentZone = currentCity && citiesMapper.findZone(queryParams[0], currentCity.city_zones) // validate zone over the current city

  return queryParams.length === 1 && !!currentZone?.slug
}

const isDetailPage = (locale: Locale, city, currentZone, queryParams) => {
  const isOnCityWithoutZone =
    queryParams.length === 1 && currentZone === undefined && !isFilteredPage(locale, queryParams)
  const isOnCityWithZone = queryParams.length === 2 && currentZone !== undefined && currentZone.slug !== undefined
  const isNotOnValidCity = !listingFacilities.validateCity(city as string, locale)

  return isOnCityWithoutZone || isOnCityWithZone || isNotOnValidCity
}

export const getPageTypeFromParams = (locale: Locale, city, currentZone, queryParams, cities: CateCity[]) => {
  const isOnFilteredPage = isFilteredPage(locale, queryParams)
  const isOnZonePage = isZonePage(city, queryParams, cities)
  const isOnDetailPage = isDetailPage(locale, city, currentZone, queryParams)

  const isCityValid = findCity(city, cities)
  if (!isCityValid || !(isOnFilteredPage || isOnZonePage || isOnDetailPage)) {
    return 'not-found'
  }
  if (isOnFilteredPage) {
    return 'canonical-filter'
  }
  if (isOnZonePage) {
    return 'zone'
  }
  if (isOnDetailPage) {
    return 'detail'
  }
  return 'not-found'
}

export const __test = {
  isFilteredPage,
}
